import React from 'react';
import { Link, useLocation, useParams, useSearchParams } from 'react-router-dom';
import { Subcategories } from '../../../ui/navigation/Subcategories/Subcategories';
import { Subcategory } from '../../../ui/navigation/Subcategories/styles';
import { Tab, Tabs } from '../../../ui/navigation/Tabs';
import { canViewBusinessAsAdmin, isBloggerBusiness } from '../../../utils/accessService';
import { useTranslation } from '../../../utils/useTranslation';
import { useCurrentUser } from '../../users/useCurrentUser';
import { useBusinessModel } from '../useBusinessModel';
import { useBloggerTabRedirects } from './hokks/useTabRedirects';
import { TabText } from './styles';
import { BusinessDetailsTabsProps } from './types';
import { buildBloggerTabs, buildSubcategories } from './utils/buildBloggerTabs';
import { buildBusinessTabs } from './utils/buildBusinessTabs';
import { getTabsValue } from './utils/getTabsValue';

export const BusinessDetailsTabs = ({ onTabClick }: BusinessDetailsTabsProps) => {
    const params = useParams();

    const t = useTranslation();

    const businessId = params.businessId!;
    const business = useBusinessModel({ businessId });

    const [query] = useSearchParams();
    const { pathname } = useLocation();
    const currentUser = useCurrentUser();

    const { tabsValue, subcategoriesValue } = getTabsValue(pathname, query);
    const { tabsHidden, subcategoriesHidden } = useBloggerTabRedirects(tabsValue);
    const isAdmin = canViewBusinessAsAdmin(business?.id, currentUser);

    if (!isAdmin && !isBloggerBusiness(business?.type)) {
        return null;
    }

    if (isBloggerBusiness(business?.type)) {
        return (
            <>
                {!tabsHidden && (
                    <Tabs px={{ xs: 1, sm: 3 }} value={tabsValue} onChange={onTabClick}>
                        {buildBloggerTabs(businessId).map((tabItem) => (
                            <Tab
                                key={tabItem.value}
                                as={Link}
                                width="50%"
                                replace
                                to={tabItem.link}
                                value={tabItem.value}
                                textDecoration="none"
                            >
                                <TabText>{t(tabItem.label)}</TabText>
                            </Tab>
                        ))}
                    </Tabs>
                )}
                {isAdmin && !subcategoriesHidden ? (
                    <Subcategories p={2} justifySelf={'center'} value={subcategoriesValue}>
                        {buildSubcategories(businessId, tabsValue).map((subcategory, i) => (
                            <Subcategory
                                key={subcategory.value}
                                ml={i === 0 ? undefined : 1}
                                as={Link}
                                replace
                                to={subcategory.link}
                                value={subcategory.value}
                                textDecoration="none"
                                size="m"
                            >
                                {t(subcategory.label)}
                            </Subcategory>
                        ))}
                    </Subcategories>
                ) : null}
            </>
        );
    }

    return (
        <Tabs px={{ xs: 1, sm: 3 }} value={pathname} onChange={onTabClick}>
            {buildBusinessTabs(businessId, t).map((tabItem) => (
                <Tab
                    key={tabItem.value}
                    as={Link}
                    width="50%"
                    replace
                    to={tabItem.value}
                    value={tabItem.value}
                    textDecoration="none"
                >
                    <TabText>{tabItem.label}</TabText>
                </Tab>
            ))}
        </Tabs>
    );
};
